import { useStoredState } from "@/hooks/useStoredState";
import { ToolShell } from "@/components/tools/ToolShell";
import { AutoTextarea, TextField } from "@/components/tools/fields";
import { Progress, SubHead } from "@/components/tools/blocks";
import { TickList } from "@/components/tools/ProvisionReview";

const STAGES = [
  { id: "before", title: "Before the Meeting", items: ["Review date identified", "Invitations sent", "Parent views requested", "Child views requested", "Staff reports requested", "External professional reports requested", "Venue confirmed", "Documentation circulated"] },
  { id: "during", title: "During the Meeting", items: ["Progress reviewed", "Outcomes discussed", "Provision reviewed", "Parent views recorded", "Child views recorded", "Recommendations agreed"] },
  { id: "after", title: "After the Meeting", items: ["Notes completed", "Actions assigned", "Local Authority notified", "Review submitted", "Follow-up meeting scheduled"] },
];
const RECORD_TEXT = [
  { k: "strengths", label: "Strengths" },
  { k: "concerns", label: "Concerns" },
  { k: "recommendations", label: "Recommendations" },
  { k: "actions", label: "Actions" },
];

export const EhcpChecklist = () => {
  const [data, setData, reset] = useStoredState("ehcp-toolkit-v3", { ticks: {}, notes: {}, record: {} });
  const r = data.record || {};
  const setRec = (k, v) => setData((d) => ({ ...d, record: { ...d.record, [k]: v } }));
  const total = STAGES.reduce((s, st) => s + st.items.length, 0);
  const done = STAGES.reduce((s, st) => s + Object.values(data.ticks?.[st.id] || {}).filter(Boolean).length, 0);
  return (
    <ToolShell id="ehcp-toolkit" onReset={reset}>
      <Progress done={done} total={total} label="checklist items complete" testid="ehcp-progress-text" />
      {STAGES.map((st, i) => (
        <div key={st.id}>
          <SubHead letter={String.fromCharCode(65 + i)} title={st.title} />
          <TickList name={`ehcp-${st.id}`} items={st.items} value={data.ticks?.[st.id]} onChange={(v) => setData((d) => ({ ...d, ticks: { ...d.ticks, [st.id]: v } }))} />
          <div className="mt-4">
            {st.id === "after" ? (
              <div className="max-w-xs"><TextField id="ehcp-completion" label="Completion date" type="date" value={data.completion} onChange={(v) => setData((d) => ({ ...d, completion: v }))} testid="ehcp-completion" /></div>
            ) : (
              <AutoTextarea id={`ehcp-${st.id}-notes`} label="Notes" rows={2} value={data.notes?.[st.id]} onChange={(v) => setData((d) => ({ ...d, notes: { ...d.notes, [st.id]: v } }))} testid={`ehcp-${st.id}-notes`} />
            )}
          </div>
        </div>
      ))}

      <SubHead letter="D" title="Review Meeting Record" />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField id="ehcp-r-year" label="Year group" value={r.year} onChange={(v) => setRec("year", v)} testid="ehcp-r-year" />
        <TextField id="ehcp-r-reviewDate" label="EHCP review date" type="date" value={r.reviewDate} onChange={(v) => setRec("reviewDate", v)} testid="ehcp-r-reviewDate" />
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {RECORD_TEXT.map((f) => <AutoTextarea key={f.k} id={`ehcp-r-${f.k}`} label={f.label} rows={3} value={r[f.k]} onChange={(v) => setRec(f.k, v)} testid={`ehcp-r-${f.k}`} />)}
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <TextField id="ehcp-r-person" label="Responsible person" value={r.person} onChange={(v) => setRec("person", v)} testid="ehcp-r-person" />
        <TextField id="ehcp-r-next" label="Review date" type="date" value={r.next} onChange={(v) => setRec("next", v)} testid="ehcp-r-next" />
      </div>
    </ToolShell>
  );
};
